/**
 * Format currency amounts in Indian number format with abbreviations.
 *
 * Below 1,000      → as-is       (₹750)
 * 1,000–99,999     → thousands   (₹1.5K, ₹45.2K)
 * 1,00,000–99,99,999 → lakhs    (₹1.5L, ₹23.4L)
 * 1,00,00,000+     → crores      (₹1.2Cr, ₹5.8Cr)
 */

const CRORE = 1_00_00_000;
const LAKH = 1_00_000;
const THOUSAND = 1_000;

export function formatCurrencyWhole(amount, symbol = '₹') {
  const num = Math.round(parseFloat(amount) || 0);
  const sign = num < 0 ? '-' : '';
  return `${sign}${symbol}${Math.abs(num).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function parseMoneyNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value == null || value === '') return null;
  const cleaned = String(value).replace(/₹/g, '').replace(/Rs\.?/gi, '').replace(/,/g, '').trim();
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

const MONEY_LABEL_RE = /(amount|charges?|freight|discount|paid|to ?pay|topay|commission|tds|revenue|salary|expense|exp\b|hire|nrr|settlement|cost|balance|receivable|on account|gross business|intransit|in.?transit|payment received|unpaid|net amount|handling|outstanding|refund|recovery|profit|loss|owner|interest|\bnet\b|\bdue\b|rs\.?|₹|cum\.?\s*total|total amt|sgst|cgst|igst|\bgst\b|insurance|pickup|delivery charge|sub\s*total|total\s*\()/i;

export function isMoneyLabel(label) {
  const text = String(label || '').replace(/[_-]+/g, ' ').trim();
  if (/^totals?$/i.test(text) || /^net$/i.test(text)) return true;
  if (/^\d{1,2}\s+[A-Za-z]{3}$/.test(text)) return true;
  return MONEY_LABEL_RE.test(text);
}

export function formatLabeledMoney(label, value, symbol = '₹') {
  if (value == null || value === '') return value;
  if (!isMoneyLabel(label)) return value;
  const num = parseMoneyNumber(value);
  if (num == null) return value;
  return formatCurrencyWhole(num, symbol);
}

export function formatReportCell(label, value, empty = '') {
  if (value == null || value === '') return empty;
  const text = String(label || '').replace(/[_-]+/g, ' ').trim();
  if (/^tds$/i.test(text)) {
    const num = parseMoneyNumber(value);
    if (num == null) return empty === '' ? String(value) : empty;
    const sign = num < 0 ? '-' : '';
    return `${sign}₹${Math.abs(num).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  const formatted = formatLabeledMoney(label, value);
  return formatted == null || formatted === '' ? empty : String(formatted);
}

export function formatCurrency(amount, symbol = '₹') {
  const num = parseFloat(amount) || 0;
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  let formatted;
  if (abs >= CRORE) {
    formatted = `${(abs / CRORE).toFixed(1)}Cr`;
  } else if (abs >= LAKH) {
    formatted = `${(abs / LAKH).toFixed(1)}L`;
  } else if (abs >= THOUSAND) {
    formatted = `${(abs / THOUSAND).toFixed(1)}K`;
  } else {
    formatted = abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  return `${sign}${symbol}${formatted}`;
}

export function fullCurrency(amount, symbol = '₹') {
  const num = parseFloat(amount) || 0;
  return `${symbol}${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function CurrencyDisplay({ amount, symbol = '₹', className = '' }) {
  return (
    <span className={className} title={fullCurrency(amount, symbol)}>
      {formatCurrency(amount, symbol)}
    </span>
  );
}

export default formatCurrency;
